import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue'

import type { ReportRow } from '../models/report.types'
import type {
  ReportTableConfigV1,
  ReportTablePreferenceV1,
  ReportTableSortV1,
  ResolvedReportTableConfig,
} from '../models/tableConfig.types'
import {
  applyReportTablePreference,
  findScheduleTabTableConfig,
  resolveReportTableConfig,
} from '../service/reportTableConfig'

import { useReportSchedules } from './useReportSchedules'
import { useReportTableViewState } from './useReportTableViewState'

import type { SortState, TableColumn } from '@/shared/ui'

export interface UseDataVisibleTableStateInput {
  id: MaybeRefOrGetter<string>
  reportId: MaybeRefOrGetter<number>
  partitionName: MaybeRefOrGetter<string | undefined>
  configuredSortField: MaybeRefOrGetter<string | undefined>
  configuredSortOrder: MaybeRefOrGetter<1 | -1>
  configuredTable: MaybeRefOrGetter<ReportTableConfigV1 | undefined>
  columns: MaybeRefOrGetter<TableColumn<ReportRow>[]>
}

export interface UseDataVisibleTableState {
  config: ComputedRef<ResolvedReportTableConfig>
  columns: ComputedRef<TableColumn<ReportRow>[]>
  sort: ComputedRef<ReportTableSortV1 | null>
  tableSort: ComputedRef<SortState>
  hasPreference: ComputedRef<boolean>
  hydrate: (preference: Partial<ReportTablePreferenceV1>) => void
  setSort: (value: SortState) => ReportTableSortV1 | null
  setColumnOrder: (columns: TableColumn<ReportRow>[]) => string[]
  setColumnWidths: (columns: TableColumn<ReportRow>[]) => Record<string, number>
}

/** Resolves product config and durable user preference without coupling request state to table UI. */
export function useDataVisibleTableState(
  input: UseDataVisibleTableStateInput,
): UseDataVisibleTableState {
  const schedules = useReportSchedules()
  const localConfig = computed<ReportTableConfigV1>(() => {
    const configured = toValue(input.configuredTable)
    const hasConfiguredDefault = configured !== undefined && 'defaultSort' in configured
    const sortField = toValue(input.configuredSortField)
    return {
      version: 1,
      ...(configured ?? {}),
      ...(!hasConfiguredDefault && sortField
        ? { defaultSort: { field: sortField, order: toValue(input.configuredSortOrder) } }
        : {}),
    }
  })
  const scheduleConfig = computed(() => {
    const schedule = schedules.getScheduleByReportId(toValue(input.reportId))
    return findScheduleTabTableConfig(schedule?.additional_info?.tabs, toValue(input.partitionName))
  })
  const config = computed(() => resolveReportTableConfig(localConfig.value, scheduleConfig.value))
  const view = useReportTableViewState({
    id: input.id,
    partitionName: input.partitionName,
    columns: computed(() => toValue(input.columns)),
    config,
  })
  const columns = computed(() =>
    applyReportTablePreference(toValue(input.columns), view.preference.value),
  )
  const tableSort = computed<SortState>(() => {
    const value = view.sort.value
    return value ? { field: value.field, dir: value.order === 1 ? 'asc' : 'desc' } : null
  })

  function setSort(value: SortState): ReportTableSortV1 | null {
    view.setSort(value ? { field: value.field, order: value.dir === 'asc' ? 1 : -1 } : null)
    return view.sort.value
  }

  function setColumnOrder(nextColumns: TableColumn<ReportRow>[]): string[] {
    view.setColumnOrder(nextColumns.map((column) => column.field))
    return view.preference.value.columnOrder
  }

  function setColumnWidths(nextColumns: TableColumn<ReportRow>[]): Record<string, number> {
    view.setColumnWidths(
      Object.fromEntries(
        nextColumns.flatMap((column) =>
          column.width === undefined ? [] : ([[column.field, column.width]] as const),
        ),
      ),
    )
    return view.preference.value.columnWidths
  }

  return {
    config,
    columns,
    sort: computed(() => view.sort.value),
    tableSort,
    hasPreference: view.hasPreference,
    hydrate: (next) =>
      view.hydrate({
        ...view.preference.value,
        ...next,
        version: 1,
        columnOrder: next.columnOrder ?? view.preference.value.columnOrder,
        columnWidths: next.columnWidths ?? view.preference.value.columnWidths,
        sort: 'sort' in next ? (next.sort ?? null) : view.preference.value.sort,
      }),
    setSort,
    setColumnOrder,
    setColumnWidths,
  }
}
